import React, { useState } from "react";
import {
  TextField,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Box,
  Divider,
  Tooltip,
  Alert,
  Grid,
  Container
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";

// Import icons for enhanced UI
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import LanguageIcon from "@mui/icons-material/Language";
import WebIcon from "@mui/icons-material/Web";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import SecurityIcon from "@mui/icons-material/Security";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

interface NewAppFormProps {
  onCancel: () => void;
  onSubmit: (data: any) => void;
}

const NewAppForm: React.FC<NewAppFormProps> = ({ onCancel, onSubmit }) => {
  const [displayName, setDisplayName] = useState("");
  const [signInAudience, setSignInAudience] = useState("AzureADMyOrg");

  const [enableSpa, setEnableSpa] = useState<boolean>(false);
  const [spaRedirectUri, setSpaRedirectUri] = useState("");

  const [enableWeb, setEnableWeb] = useState<boolean>(false);
  const [webRedirectUri, setWebRedirectUri] = useState("");

  const [includeDefaultUserRole, setIncludeDefaultUserRole] = useState(true);

  // Validation states
  const [displayNameError, setDisplayNameError] = useState("");
  const [spaRedirectError, setSpaRedirectError] = useState("");
  const [webRedirectError, setWebRedirectError] = useState("");

  // Add this validation state to track platform selection errors
  const [platformError, setPlatformError] = useState("");

  const [enableApi, setEnableApi] = useState<boolean>(false);
  const [apiScope, setApiScope] = useState({
    scopeName: "user_impersonation",
    scopeDescription: "Access the API as the signed-in user"
  });
  const [apiScopeError, setApiScopeError] = useState("");

  // Validate as user types
  const validateDisplayName = (value: string) => {
    if (!value.trim()) {
      setDisplayNameError("Display Name is required");
    } else {
      setDisplayNameError("");
    }
  };

  const validateSpaRedirect = (value: string) => {
    if (enableSpa && !value.trim()) {
      setSpaRedirectError("Redirect URI is required when SPA is enabled");
    } else {
      setSpaRedirectError("");
    }
  };

  const validateWebRedirect = (value: string) => {
    if (enableWeb && !value.trim()) {
      setWebRedirectError("Redirect URI is required when Web is enabled");
    } else {
      setWebRedirectError("");
    }
  }; 

  const validateApiScope = (scopeName: string): string => {
    if (!scopeName.trim()) {
      return "Scope name is required";
    } else if (!/^[a-zA-Z0-9_.]+$/.test(scopeName)) {
      // Updated regex to allow dots for standard Azure scope naming convention
      return "Scope name must contain only letters, numbers, underscores, and dots";
    }
    return "";
  };  

  const handleCreate = () => {
    // 1) Validate mandatory fields
    validateDisplayName(displayName);
    validateSpaRedirect(spaRedirectUri);
    validateWebRedirect(webRedirectUri);
    
    // Validate that at least one platform is selected
    if (!enableSpa && !enableWeb && !enableApi) {
      setPlatformError("At least one platform must be selected");
      return;
    } else {
      setPlatformError("");
    }
  
    // Other validation checks
    if (!displayName.trim() || 
        (enableSpa && !spaRedirectUri.trim()) || 
        (enableWeb && !webRedirectUri.trim()) ||
        (enableApi && (!apiScope.scopeName.trim() || !!apiScopeError))) {
      return;
    }

    // 2) Build base data
    const baseData = {
      displayName,
      signInAudience
    };

    const newAppData: any = { ...baseData };

    if (enableSpa) {
      newAppData.spa = {
        redirectUris: [spaRedirectUri.trim()]
      };
    }
    if (enableWeb) {
      newAppData.web = {
        redirectUris: [webRedirectUri.trim()]
      };
    }
    if (enableApi) {
      // Generate scope ID
      const scopeId = uuidv4();
      
      // CHANGE: Use appId format instead of display name
      // The actual appId will be assigned by Azure, but we'll indicate this pattern
      newAppData.api = {
        acceptMappedClaims: null,
        knownClientApplications: [],
        oauth2PermissionScopes: [
          {
            adminConsentDescription: apiScope.scopeDescription,
            adminConsentDisplayName: apiScope.scopeName,
            id: scopeId,
            isEnabled: true,
            type: "User",
            userConsentDescription: apiScope.scopeDescription,
            userConsentDisplayName: apiScope.scopeName,
            value: apiScope.scopeName
          }
        ],
        requestedAccessTokenVersion: 2
      };
      
      // We'll set an empty identifierUris array
      // Azure will automatically assign the api://<appId> format after creation
      // This follows Microsoft's best practice for new applications
      newAppData.identifierUris = [];
    }

    // 3) Optionally add the DefaultUser role, with a new GUID
    if (includeDefaultUserRole) {
      const defaultUserRole = {
        id: uuidv4(), 
        allowedMemberTypes: ["User"],
        description: "Default user role for the app",
        displayName: "Default User",
        isEnabled: true,
        value: "DefaultUser"
      };
      newAppData.appRoles = [defaultUserRole];
    }

    // 4) Pass final object to parent
    onSubmit(newAppData);
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            onClick={onCancel}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                borderColor: "primary.main",
                color: "primary.main"
              }
            }}
          >
            Back
          </Button>
          
          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              Register New Application
            </Typography>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Create a new app registration in Azure Active Directory
            </Typography>
          </Box>
        </Box>
      </Card>
      
      {/* Main Form Card */}
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          mb: 3
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Typography
            variant="h6"
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid rgba(0,0,0,0.12)',
              backgroundColor: '#f9f9f9',
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <AppRegistrationIcon sx={{ mr: 1, color: 'primary.main' }} />
            Basic Application Details
          </Typography>
          
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Display Name"
                  value={displayName}
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                    validateDisplayName(e.target.value);
                  }}
                  required
                  fullWidth
                  variant="outlined"
                  error={!!displayNameError}
                  helperText={displayNameError}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                  <InputLabel>Sign-In Audience</InputLabel>
                  <Select
                    label="Sign-In Audience"
                    value={signInAudience}
                    onChange={(e) => setSignInAudience(e.target.value as string)}
                  >
                    <MenuItem value="AzureADMyOrg">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LanguageIcon sx={{ mr: 1, color: 'primary.light', fontSize: '1.2rem' }} />
                        <Typography>Accounts in this directory only</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="AzureADMultipleOrgs">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LanguageIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1.2rem' }} />
                        <Typography>Accounts in any org directory</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="AzureADandPersonalMicrosoftAccount">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LanguageIcon sx={{ mr: 1, color: 'secondary.main', fontSize: '1.2rem' }} />
                        <Typography>Accounts in any directory + personal</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="PersonalMicrosoftAccount">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LanguageIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                        <Typography>Personal MS accounts only</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
      
      {/* Platforms Card */}
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          mb: 3,
          // Add red border if there's a platform error
          borderColor: platformError ? 'error.main' : 'divider'
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Typography
            variant="h6"
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid rgba(0,0,0,0.12)',
              backgroundColor: platformError ? 'rgba(253, 237, 237, 0.5)' : '#f9f9f9',
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between' // Added to place error indicator
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DevicesOtherIcon sx={{ mr: 1, color: platformError ? 'error.main' : 'primary.main' }} />
              Application Platforms
            </Box>
            
            {/* Add required indicator */}
            <Typography 
              variant="caption" 
              sx={{ 
                color: platformError ? 'error.main' : 'text.secondary',
                fontWeight: platformError ? 600 : 400
              }}
            >
              * Required
            </Typography>
          </Typography>
          
          <Box sx={{ p: 3 }}>
            {/* SPA Platform */}
            <Box sx={{ mb: enableSpa ? 4 : 2, py: 1 }}>
              <FormControlLabel
                control={
                <Checkbox
                  checked={enableSpa}
                  onChange={(e) => {
                    setEnableSpa(e.target.checked);
                    if (e.target.checked) {
                      // Clear platform error when selected
                      setPlatformError("");
                    } else if (!enableWeb) {
                      // If this is being unselected and the other isn't selected, show warning
                      setPlatformError("At least one platform must be selected");
                    }
                    if (!e.target.checked) {
                      setSpaRedirectError("");
                    }
                  }}
                  color="primary"
                />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <WebIcon sx={{ mr: 1, color: enableSpa ? 'primary.main' : 'text.disabled' }} />
                    <Typography sx={{ fontWeight: 500 }}>
                      Enable Single Page Application (SPA)
                    </Typography>
                    <Tooltip title="Use for JavaScript applications running in a browser" arrow>
                      <InfoOutlinedIcon sx={{ ml: 1, fontSize: '1rem', color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                }
              />
              
              {enableSpa && (
                <Box sx={{ mt: 2, ml: 4 }}>
                  <TextField
                    label="SPA Redirect URI"
                    value={spaRedirectUri}
                    onChange={(e) => {
                      setSpaRedirectUri(e.target.value);
                      validateSpaRedirect(e.target.value);
                    }}
                    placeholder="https://localhost:3000"
                    fullWidth
                    variant="outlined"
                    error={!!spaRedirectError}
                    helperText={spaRedirectError || "Where users will be sent after authentication"}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
            
            {enableSpa && <Divider sx={{ my: 2 }} />}
            
            {/* Web Platform */}
            <Box sx={{ mb: enableWeb ? 4 : 2, py: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enableWeb}
                    onChange={(e) => {
                      setEnableWeb(e.target.checked);
                      if (e.target.checked) {
                        // Clear platform error when selected
                        setPlatformError("");
                      } else if (!enableSpa) {
                        // If this is being unselected and the other isn't selected, show warning
                        setPlatformError("At least one platform must be selected");
                      }
                      if (!e.target.checked) {
                        setWebRedirectError("");
                      }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LanguageIcon sx={{ mr: 1, color: enableWeb ? 'primary.main' : 'text.disabled' }} />
                    <Typography sx={{ fontWeight: 500 }}>
                      Enable Web Application
                    </Typography>
                    <Tooltip title="Use for server-side web applications" arrow>
                      <InfoOutlinedIcon sx={{ ml: 1, fontSize: '1rem', color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                }
              />
              
              {enableWeb && (
                <Box sx={{ mt: 2, ml: 4 }}>
                  <TextField
                    label="Web Redirect URI"
                    value={webRedirectUri}
                    onChange={(e) => {
                      setWebRedirectUri(e.target.value);
                      validateWebRedirect(e.target.value);
                    }}
                    placeholder="https://localhost:5000"
                    fullWidth
                    variant="outlined"
                    error={!!webRedirectError}
                    helperText={webRedirectError || "Where users will be sent after authentication"}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* API Platform */}
            <Box sx={{ mb: enableApi ? 4 : 2, py: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enableApi}
                    onChange={(e) => {
                      setEnableApi(e.target.checked);
                      if (e.target.checked) {
                        // Clear platform error when selected
                        setPlatformError("");
                      } else if (!enableSpa && !enableWeb) {
                        // If this is being unselected and the others aren't selected, show warning
                        setPlatformError("At least one platform must be selected");
                      }
                      if (!e.target.checked) {
                        setApiScopeError("");
                      }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SecurityIcon sx={{ mr: 1, color: enableApi ? 'primary.main' : 'text.disabled' }} />
                    <Typography sx={{ fontWeight: 500 }}>
                      Enable API
                    </Typography>
                    <Tooltip title="Use for RESTful APIs that other applications can consume" arrow>
                      <InfoOutlinedIcon sx={{ ml: 1, fontSize: '1rem', color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                }
              />
              
              {enableApi && (
                <Box sx={{ mt: 2, ml: 4 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    API Scope Configuration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Scope Name"
                        value={apiScope.scopeName}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setApiScope({...apiScope, scopeName: newValue});
                          setApiScopeError(validateApiScope(newValue));
                        }}
                        placeholder="Files.Read"
                        fullWidth
                        variant="outlined"
                        error={!!apiScopeError}
                        helperText={apiScopeError || "Name of the OAuth 2.0 scope (e.g., Files.Read, user_impersonation)"}
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Scope Description"
                        value={apiScope.scopeDescription}
                        onChange={(e) => setApiScope({...apiScope, scopeDescription: e.target.value})}
                        placeholder="Access the API as the signed-in user"
                        fullWidth
                        variant="outlined"
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Alert 
                    severity="info" 
                    sx={{ mt: 2, borderRadius: 2 }}
                  >
                    <Typography variant="body2">
                      This will create an OAuth 2.0 permission scope that client applications can request.
                      The scope will be exposed at <code>api://&lt;appId&gt;/{apiScope.scopeName}</code> where appId will be generated during creation.
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Box>

            {enableApi && <Divider sx={{ my: 2 }} />}            
            
            {!enableSpa && !enableWeb && !enableApi && (
              <Alert 
                severity={platformError ? "error" : "warning"} 
                sx={{ mt: 2, mb: 1, borderRadius: 2 }}
              >
                {platformError || "You must enable at least one platform to create a functional application."}
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>
      
      {/* App Roles Card */}
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          mb: 4
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Typography
            variant="h6"
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid rgba(0,0,0,0.12)',
              backgroundColor: '#f9f9f9',
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
            App Roles
          </Typography>
          
          <Box sx={{ p: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeDefaultUserRole}
                  onChange={(e) => setIncludeDefaultUserRole(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontWeight: 500 }}>Include DefaultUser App Role</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    <b>Recommended for APIs: include an app role if you plan to enforce authorization using role claims in access tokens.</b>
                  </Typography>
                </Box>
              }
            />
            
            {includeDefaultUserRole && (
              <Box 
                sx={{ 
                  mt: 2, 
                  p: 2, 
                  backgroundColor: 'rgba(227, 242, 253, 0.5)', 
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'flex-start'
                }}
              >
                <Box sx={{ mr: 2, mt: 0.5 }}>
                  <SecurityIcon color="primary" fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Default User</Typography>
                  <Typography variant="body2" color="text.secondary">Role Value: <code>DefaultUser</code></Typography>
                  <Typography variant="body2" color="text.secondary">Description: Default user role for the app</Typography>
                </Box>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
      
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, mb: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{ 
            mr: 2,
            borderRadius: 2
          }}
        >
          Cancel
        </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            startIcon={<AddCircleOutlineIcon />}
            disabled={
              !displayName.trim() || // Explicitly check for empty display name
              !!displayNameError || 
              !!spaRedirectError || 
              !!webRedirectError || 
              !!apiScopeError ||
              !!platformError || 
              (!enableSpa && !enableWeb && !enableApi)
            }
            sx={{ 
              borderRadius: 2,
              px: 3,
              boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
                boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)'
              }
            }}
          >
            Create Application
          </Button>
      </Box>
      
      {/* Info Box */}
      <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#f9f9f9', mt: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">About App Registration</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(232, 245, 233, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#2e7d32' }}>
                  What is an App Registration?
                </Typography>
                <Typography variant="body2">
                  An app registration establishes an identity configuration for your application
                  that allows it to integrate with Azure AD authentication.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(227, 242, 253, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1976d2' }}>
                  Redirect URIs
                </Typography>
                <Typography variant="body2">
                  These URIs are where Azure AD will send the authentication response.
                  For local development, use localhost addresses.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255, 243, 224, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#e65100' }}>
                  App Roles
                </Typography>
                <Typography variant="body2">
                  App roles define permissions that can be assigned to users or service principals,
                  and are included in tokens issued to authenticated users.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default NewAppForm;